import { ObjectData } from 'types/UtilityTypes';
import { TMS, TMSTimezoneConfig } from 'types/enums/Integrations';

import { parseToUtcWallClockDate } from './parseToUtcWallClockDate';
import { denormalizeUTCDate, normalizeUTCDate } from './time';

/**
 * Glossary:
 * - Normalize: Convert a date to UTC
 * - Denormalize: Convert a date (usually UTC) to the user's timezone
 */

/**
 * Converts DateTime fields on a flat object for TMS submission.
 * Applies TMS timezone config: for timezone-agnostic TMSes, dates are normalized to UTC;
 * otherwise the date is kept as-is.
 *
 * normalizeDatesForTMSForm does not recurse into nested objects—fields like
 * `pickup` or `consignee` that contain their own DateTime fields are left unchanged.
 */
export function normalizeDatesForTMSForm(tmsName: string, data: ObjectData) {
  const finalObject: ObjectData = {};

  Object.keys(data).forEach((field) => {
    let value = data[field];

    const isTimeField =
      field.endsWith('Start') ||
      field.endsWith('End') ||
      field.endsWith('Time') ||
      field === 'mustDeliver';
    // 3G only: openTime/closeTime are HH:MM (time-of-day), not full datetime; other TMSes use datetime.
    const isTimeOfDayOnly =
      (tmsName as TMS) === TMS.ThreeG &&
      (field === 'openTime' || field === 'closeTime');

    if (value && isTimeField && !isTimeOfDayOnly) {
      if (value !== '0001-01-01T00:00:00Z') {
        if (
          tmsName === TMS.Revenova &&
          (field === 'readyTime' || field === 'mustDeliver')
        ) {
          value = parseToUtcWallClockDate(value);
        } else {
          // NOTE: Some TMSes have are timezone agnostic, so the backend defaults datetime
          // conversions to UTC,but the front-end preserves users' current experience by
          // showing the timestamp exactly as it is written in the TMS.
          value = TMSTimezoneConfig[tmsName as TMS].isLoadTimezoneAgnostic
            ? normalizeUTCDate(new Date(value))
            : new Date(value);
        }
      } else {
        value = null;
      }
    }

    finalObject[field] = value;
  });

  return finalObject;
}

/**
 * Converts DateTime fields on a flat object for TMS submission.
 * Applies TMS timezone config: for timezone-agnostic TMSes, dates are denormalized to
 * the user's timezone; otherwise the date is kept as-is.
 *
 * denormalizeDatesForTMSForm does not recurse into nested objects—fields like
 * `pickup` or `consignee` that contain their own DateTime fields are left unchanged.
 */
export function denormalizeDatesForTMSForm(
  tmsName: string,
  data: ObjectData
): ObjectData {
  if (!data) {
    return data;
  }

  const finalObject: ObjectData = {};

  Object.keys(data).forEach((field) => {
    let value = data[field];

    const isTimeField =
      field.endsWith('Start') ||
      field.endsWith('End') ||
      field.endsWith('Time') ||
      field === 'mustDeliver';
    const isTimeOfDayOnly =
      (tmsName as TMS) === TMS.ThreeG &&
      (field === 'openTime' || field === 'closeTime');

    if (isTimeField && !isTimeOfDayOnly) {
      if (!value) {
        value = '';
      } else {
        // For Aljex and QuantumEdge loads, preserve datetime values in UTC
        value = TMSTimezoneConfig[tmsName as TMS].isLoadTimezoneAgnostic
          ? denormalizeUTCDate(new Date(value))
          : new Date(value);
      }
    }

    finalObject[field] = value;
  });

  return finalObject;
}

// Pure helper for load/appt dates. Pass tmsName explicitly — do not call hooks here.
// Timezone-agnostic TMSes keep wall-clock via normalizeUTCDate; others keep the Date as-is.
export function normalizeDateForTMS(date: Date | string, tmsName: string) {
  const d = typeof date === 'string' ? new Date(date) : date;
  const config = TMSTimezoneConfig[tmsName as TMS];

  return config?.isLoadTimezoneAgnostic ? normalizeUTCDate(d) : d;
}
